import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../useAuth";
import { canAccessAdminModules } from "../../accessControl";
import {
  archiveRiskCategory,
  createRiskCategory,
  listRiskCategories,
  updateRiskCategory,
} from "../../api/riskCategories.api";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import Select from "../../ui/Select";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { spacing } from "../../ui/tokens";
import { buildRiskCategoryPayload, getRiskCategoryNameError } from "../../utils/riskCategoryForm";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
];

export default function RiskCategoriesAdmin() {
  const { user } = useAuth();
  const canManageAdminModules = canAccessAdminModules(user);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formState, setFormState] = useState({ name: "", description: "", status: "active" });
  const [editingId, setEditingId] = useState(null);
  const [confirmState, setConfirmState] = useState({ open: false, category: null });
  const [actionMessage, setActionMessage] = useState(null);

  const activeCount = useMemo(
    () => categories.filter((c) => c.status === "active").length,
    [categories]
  );

  const loadCategories = useCallback(() => {
    setLoading(true);
    setError(null);
    listRiskCategories({ include_archived: true })
      .then((res) => setCategories(res?.data || []))
      .catch((err) => {
        setCategories([]);
        setError(err?.response?.data?.error || err?.message || "Unable to load risk categories.");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!canManageAdminModules) return;
    loadCategories();
  }, [canManageAdminModules, loadCategories]);

  useEffect(() => {
    if (!canManageAdminModules) return undefined;
    const handleCategoriesChanged = () => loadCategories();
    window.addEventListener("riskCategoriesChanged", handleCategoriesChanged);
    return () => window.removeEventListener("riskCategoriesChanged", handleCategoriesChanged);
  }, [canManageAdminModules, loadCategories]);

  const resetForm = () => {
    setFormState({ name: "", description: "", status: "active" });
    setEditingId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = getRiskCategoryNameError(formState.name, categories, { excludeId: editingId });
    if (validationError) {
      setActionMessage({ tone: "error", text: validationError });
      return;
    }
    setSaving(true);
    setActionMessage(null);
    try {
      if (editingId) {
        await updateRiskCategory(
          editingId,
          buildRiskCategoryPayload({
            name: formState.name,
            description: formState.description,
            status: formState.status,
          })
        );
        setActionMessage({ tone: "success", text: "Category updated." });
      } else {
        await createRiskCategory(
          buildRiskCategoryPayload({
            name: formState.name,
            description: formState.description,
            status: formState.status,
          })
        );
        setActionMessage({ tone: "success", text: "Category created." });
      }
      resetForm();
      loadCategories();
      window.dispatchEvent(new CustomEvent("riskCategoriesChanged"));
    } catch (err) {
      setActionMessage({
        tone: "error",
        text: err?.response?.data?.error || err?.message || "Unable to save category.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (category) => {
    setEditingId(category.id);
    setFormState({
      name: category.name || "",
      description: category.description || "",
      status: category.status || "active",
    });
    setActionMessage(null);
  };

  const handleArchive = async () => {
    const category = confirmState.category;
    if (!category) return;
    setSaving(true);
    setActionMessage(null);
    try {
      const res = await archiveRiskCategory(category.id);
      const payload = res?.data;
      const inUse = payload?.in_use;
      setActionMessage({
        tone: "success",
        text: inUse
          ? "Category archived. It remains assigned to existing risks."
          : "Category archived.",
      });
      loadCategories();
      window.dispatchEvent(new CustomEvent("riskCategoriesChanged"));
    } catch (err) {
      setActionMessage({
        tone: "error",
        text: err?.response?.data?.error || err?.message || "Unable to archive category.",
      });
    } finally {
      setSaving(false);
      setConfirmState({ open: false, category: null });
    }
  };

  if (!canManageAdminModules) {
    return (
      <div style={guardStyle}>
        <h2 style={{ marginTop: 0 }}>Risk Categories</h2>
        <p style={{ marginBottom: 0 }}>
          You need Manage access to Admin Module Portal to manage risk categories.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: spacing.xl }}>
      <div>
        <h2 style={{ marginBottom: spacing.xs }}>Risk Categories</h2>
        <p style={{ margin: 0, color: "#6b7280", fontSize: 13 }}>
          Configure the categories used across the Risk Register. Archived categories remain visible but are not available
          for new risks.
        </p>
      </div>

      <div style={summaryCardStyle}>
        <div style={{ fontWeight: 600 }}>Active categories</div>
        <div style={{ fontSize: 28 }}>{activeCount}</div>
      </div>

      <div style={formCardStyle}>
        <h3 style={{ marginTop: 0 }}>{editingId ? "Edit category" : "Add category"}</h3>
        <form onSubmit={handleSubmit} style={{ display: "grid", gap: spacing.md }}>
          <Input
            label="Category name"
            value={formState.name}
            onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., Information Security"
          />
          <Input
            label="Description"
            value={formState.description}
            onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Optional description"
          />
          <label style={fieldLabel}>
            <span style={labelText}>Status</span>
            <Select
              value={formState.status}
              onChange={(e) => setFormState((prev) => ({ ...prev, status: e.target.value }))}
              style={inputStyle}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </label>

          {actionMessage && (
            <div style={actionMessage.tone === "error" ? errorBanner : successBanner}>
              {actionMessage.text}
            </div>
          )}

          <div style={{ display: "flex", gap: spacing.sm }}>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Save changes" : "Create category"}
            </Button>
            {editingId && (
              <Button type="button" variant="secondary" onClick={resetForm}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      </div>

      <div style={tableCardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>All categories</h3>
          {loading && <span style={{ color: "#6b7280" }}>Loading...</span>}
        </div>

        {error && <div style={errorBanner}>{error}</div>}

        {!loading && !categories.length && (
          <div style={{ padding: spacing.md, color: "#6b7280" }}>
            No categories configured yet. Add your first category above.
          </div>
        )}

        {categories.length > 0 && (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Description</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => (
                <tr key={category.id}>
                  <td style={tdStyle}>{category.name}</td>
                  <td style={tdStyle}>{category.description || "-"}</td>
                  <td style={tdStyle}>
                    <span style={category.status === "archived" ? archivedTag : activeTag}>
                      {category.status}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: "flex", gap: spacing.sm }}>
                      <Button variant="secondary" onClick={() => handleEdit(category)}>
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => setConfirmState({ open: true, category })}
                        disabled={category.status === "archived"}
                      >
                        Archive
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmDialog
        open={confirmState.open}
        title="Archive category?"
        message="Archived categories remain assigned to existing risks but cannot be selected for new risks."
        confirmLabel="Archive"
        onCancel={() => setConfirmState({ open: false, category: null })}
        onConfirm={handleArchive}
      />
    </div>
  );
}

const formCardStyle = {
  background: "white",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  padding: spacing.lg,
  boxShadow: "0 8px 16px rgba(15,23,42,0.04)",
};

const tableCardStyle = {
  background: "white",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  padding: spacing.lg,
};

const summaryCardStyle = {
  background: "#f8fafc",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  padding: spacing.lg,
  display: "grid",
  gap: spacing.xs,
  maxWidth: 240,
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: spacing.md,
};

const thStyle = {
  textAlign: "left",
  fontSize: 12,
  color: "#6b7280",
  paddingBottom: spacing.sm,
  borderBottom: "1px solid #e5e7eb",
};

const tdStyle = {
  padding: `${spacing.sm}px 0`,
  borderBottom: "1px solid #f1f5f9",
  fontSize: 13,
};

const activeTag = {
  display: "inline-flex",
  padding: "2px 10px",
  borderRadius: 999,
  background: "#dcfce7",
  color: "#166534",
  fontSize: 12,
  fontWeight: 600,
};

const archivedTag = {
  display: "inline-flex",
  padding: "2px 10px",
  borderRadius: 999,
  background: "#e2e8f0",
  color: "#475569",
  fontSize: 12,
  fontWeight: 600,
};

const guardStyle = {
  padding: spacing.lg,
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  background: "#f8fafc",
};

const fieldLabel = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
};

const labelText = {
  fontSize: 12,
  color: "#6b7280",
};

const inputStyle = {
  width: "100%",
  padding: `${spacing.sm}px ${spacing.md}px`,
  borderRadius: 6,
  border: "1px solid #d1d5db",
};

const errorBanner = {
  padding: spacing.sm,
  borderRadius: 8,
  background: "#fee2e2",
  color: "#b91c1c",
  fontSize: 13,
};

const successBanner = {
  padding: spacing.sm,
  borderRadius: 8,
  background: "#dcfce7",
  color: "#166534",
  fontSize: 13,
};
